import { create } from "zustand";
import type {
  CommonGroupItem,
  CommonGroupQuery,
  CommonGroupUpsertPayload,
  CommonCodeItem,
  CommonCodeQuery,
  CommonCodeCreatePayload,
  CommonCodeUpdatePayload,
} from "src/types/common/commonCodeTypes";

// ✅ 너가 실제로 만든 api 경로로 맞춰
import {
  getCommonGroupListApi,
  createCommonGroupApi,
  updateCommonGroupApi,
  deleteCommonGroupApi,
} from "src/api/commonCode/commonGroupApi";

import {
  getCommonCodeListApi,
  createCommonCodeApi,
  updateCommonCodeApi,
  deleteCommonCodeApi,
} from "src/api/common/commonCodeApi";

type State = {
  /** ===== 그룹 (셀렉트 용) ===== */
  groupQuery: CommonGroupQuery;
  groupPage: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    content: CommonGroupItem[];
  } | null;

  /** ===== 코드 (테이블) ===== */
  selectedGrpCode: string;
  codeQuery: CommonCodeQuery;
  codePage: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    content: CommonCodeItem[];
  } | null;

  loading: boolean;
  error: string | null;

  /** ===== group actions ===== */
  setGroupQuery: (patch: Partial<CommonGroupQuery>) => void;
  fetchGroups: () => Promise<void>;
  createGroup: (payload: CommonGroupUpsertPayload) => Promise<boolean>;
  updateGroup: (grpCode: string, payload: CommonGroupUpsertPayload) => Promise<boolean>;
  deleteGroup: (grpCode: string) => Promise<boolean>;

  /** ===== code actions ===== */
  setSelectedGrpCode: (grpCode: string) => void;
  setCodeQuery: (patch: Partial<CommonCodeQuery>) => void;
  resetCodeQuery: () => void;
  fetchCodes: () => Promise<void>;
  createCode: (payload: CommonCodeCreatePayload) => Promise<boolean>;
  updateCode: (payload: CommonCodeUpdatePayload) => Promise<boolean>;
  deleteCode: (codeId: number) => Promise<boolean>;
};

const defaultGroupQuery: CommonGroupQuery = {
  grpCode: "",
  grpName: "",
  useYn: undefined,
  deleted: false,
  page: 0,
  size: 10,
  order: "codeOrder,asc",
};

const defaultCodeQuery: CommonCodeQuery = {
  code: "",
  codeName: "",
  useYn: undefined,
  deleted: false,
  page: 0,
  size: 10,
  order: "codeOrder,asc",
};

const okStatus = (res: any) => {
  const code = res?.statusCode ?? res?.status_code;
  return code === undefined || code === 0 || code === 1;
};

export const useCommonCodeStore = create<State>((set, get) => ({
  groupQuery: defaultGroupQuery,
  groupPage: null,

  selectedGrpCode: "",
  codeQuery: defaultCodeQuery,
  codePage: null,

  loading: false,
  error: null,

  /** ===== 그룹 ===== */
  setGroupQuery: (patch) =>
    set((s) => ({
      groupQuery: { ...s.groupQuery, ...patch },
    })),

  fetchGroups: async () => {
    const q = get().groupQuery;
    set({ loading: true, error: null });

    try {
      const res = await getCommonGroupListApi({
        ...q,
        grpCode: q.grpCode?.trim() || undefined,
        grpName: q.grpName?.trim() || undefined,
      });

      if (!okStatus(res)) throw new Error(res?.message || "그룹 목록 조회 실패");

      set({ groupPage: res.data, loading: false });

      // ✅ 최초 로딩 시 첫 그룹 자동 선택 (selectedGrpCode 없을 때만)
      // const first = res.data?.content?.[0]?.grpCode;
      // if (!get().selectedGrpCode && first) {
      //   set({ selectedGrpCode: first });
      // }
    } catch (e: any) {
      set({
        loading: false,
        error: e?.message || "그룹 목록 조회 중 오류",
        groupPage: null,
      });
    }
  },

  createGroup: async (payload) => {
    set({ loading: true, error: null });
    try {
      const res = await createCommonGroupApi(payload);
      if (!okStatus(res)) throw new Error(res?.message || "그룹 생성 실패");

      set({ loading: false });
      await get().fetchGroups();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "그룹 생성 중 오류" });
      return false;
    }
  },

  updateGroup: async (grpCode, payload) => {
    set({ loading: true, error: null });
    try {
      const res = await updateCommonGroupApi(grpCode, payload);
      if (!okStatus(res)) throw new Error(res?.message || "그룹 수정 실패");

      set({ loading: false });
      await get().fetchGroups();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "그룹 수정 중 오류" });
      return false;
    }
  },

  deleteGroup: async (grpCode) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteCommonGroupApi(grpCode);
      if (!okStatus(res)) throw new Error(res?.message || "그룹 삭제 실패");

      // 삭제한 그룹이 선택중이면 선택 해제
      if (get().selectedGrpCode === grpCode) set({ selectedGrpCode: "", codePage: null });

      set({ loading: false });
      await get().fetchGroups();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "그룹 삭제 중 오류" });
      return false;
    }
  },

  /** ===== 코드 ===== */
  setSelectedGrpCode: (grpCode) => set({ selectedGrpCode: grpCode }),

  setCodeQuery: (patch) =>
    set((s) => ({
      codeQuery: { ...s.codeQuery, ...patch },
    })),

  resetCodeQuery: () => set({ codeQuery: { ...defaultCodeQuery } }),

  fetchCodes: async () => {
    const grpCode = get().selectedGrpCode;
    const q = get().codeQuery;

    if (!grpCode) {
      set({ codePage: null });
      return;
    }

    set({ loading: true, error: null });
    try {
      const res = await getCommonCodeListApi(grpCode, {
        ...q,
        code: q.code?.trim() || undefined,
        codeName: q.codeName?.trim() || undefined,
      });

      if (!okStatus(res)) throw new Error(res?.message || "코드 목록 조회 실패");
      set({ codePage: res.data, loading: false });
    } catch (e: any) {
      set({
        loading: false,
        error: e?.message || "코드 목록 조회 중 오류",
        codePage: null,
      });
    }
  },

  createCode: async (payload) => {
    set({ loading: true, error: null });
    try {
      // ✅ 서버 스펙: 배열로 생성
      const res = await createCommonCodeApi([payload]);
      if (!okStatus(res)) throw new Error(res?.message || "코드 생성 실패");

      set({ loading: false });
      await get().fetchCodes();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "코드 생성 중 오류" });
      return false;
    }
  },

  updateCode: async (payload) => {
    set({ loading: true, error: null });
    try {
      // ✅ 서버 스펙: 배열로 수정
      const res = await updateCommonCodeApi([payload]);
      if (!okStatus(res)) throw new Error(res?.message || "코드 수정 실패");

      set({ loading: false });
      await get().fetchCodes();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "코드 수정 중 오류" });
      return false;
    }
  },

  deleteCode: async (codeId) => {
    set({ loading: true, error: null });
    try {
      const res = await deleteCommonCodeApi(codeId);
      if (!okStatus(res)) throw new Error(res?.message || "코드 삭제 실패");

      set({ loading: false });
      await get().fetchCodes();
      return true;
    } catch (e: any) {
      set({ loading: false, error: e?.message || "코드 삭제 중 오류" });
      return false;
    }
  },
}));
